import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration } from '../../../data/configuration.js';
import { CameraController, CameraViews } from '../../../camera-controller.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface StitchingViewProps {
	interiorConfigurator: InteriorConfigurator;
	cameraController: CameraController;
	onBackButtonClicked: () => void;
}

interface StitchingViewContextType {
	selectedStitchingItemId: string;
	handleStitchingItemClicked: (id: string) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function StitchingView(props: StitchingViewProps) {

	// initial values.
	const initialStitchingItemId = Configuration.seatStitchingOption;
	const initialStitchingItemName = () => getStitchingItemName(initialStitchingItemId);

	// states.
	const [selectedStitchingItemId, setSelectedStitchingItemId] = useState(initialStitchingItemId);
	const [selectedStitchingItemName, setSelectedStitchingItemName] = useState(initialStitchingItemName);

	// handlers.

	const handleStitchingItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedStitchingItemId(id);
		setSelectedStitchingItemName(getStitchingItemName(id));

		// update the configuration.
		Configuration.seatStitchingOption = id;
		Configuration.updateCosts();

		// select the seat stitching style on the car.
		props.interiorConfigurator.selectSeatStitchingStyle(id);

		// transition the camera to the relevant viewpoint.
		props.cameraController.changeCameraView(CameraViews.StitchingView);
	};
	
	// return the HTML.
	return (
		<StitchingViewContext.Provider value={{ selectedStitchingItemId, handleStitchingItemClicked }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-stitching">STITCHING</h2>
					</div>

					<SectionPanel
						subHeading={'STITCHING'}
						modalContent={CloudData.getModalContent(selectedStitchingItemId, CloudData.seatStitchingOptions)}
					>
						<SelectedSectionItem
							itemName={selectedStitchingItemName}
							sectionPrice={CloudData.getPrice(selectedStitchingItemId, CloudData.seatStitchingOptions, { ref: false })}
						/>
						<StitchingItems />
					</SectionPanel>
					
				</div>
			</VehicleView>
		</StitchingViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const StitchingViewContext = createContext<StitchingViewContextType | undefined>(undefined);

function StitchingItems() {
	return (
		<ViewContentRows maxRowLength={7}>
			<StitchingItem id={'S001'} imageFilePath={'black'} />
			<StitchingItem id={'S007'} imageFilePath={'black'} /><StitchingItem id={'S008'} imageFilePath={'slate'} />
			<StitchingItem id={'S028'} imageFilePath={'stone-sash'} />
			<StitchingItem id={'S006'} imageFilePath={'ice-white'} />
			<StitchingItem id={'S024'} imageFilePath={'cream'} />
			<StitchingItem id={'S005'} imageFilePath={'biscuit'} />
			<StitchingItem id={'S014'} imageFilePath={'tan'} />
			<StitchingItem id={'S025'} imageFilePath={'dark-brown'} />
			<StitchingItem id={'S031'} imageFilePath={'red'} />
			<StitchingItem id={'S002'} imageFilePath={'mulberry'} />
			<StitchingItem id={'S003'} imageFilePath={'orange'} />
			<StitchingItem id={'S004'} imageFilePath={'mustard'} />
			<StitchingItem id={'S012'} imageFilePath={'british-green'} />
			<StitchingItem id={'S011'} imageFilePath={'verdant-green'} />
			<StitchingItem id={'S023'} imageFilePath={'cotswold-green'} />
			<StitchingItem id={'S010'} imageFilePath={'turquoise'} />
			<StitchingItem id={'S013'} imageFilePath={'british-blue'} />
			<StitchingItem id={'S009'} imageFilePath={'dark-blue'} />
		</ViewContentRows>
	);
}

function StitchingItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(StitchingViewContext);
	if (!context) {
		throw new Error('StitchingViewContext must be used within a StitchingView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getStitchingItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/stitching/${props.imageFilePath}.webp`}
			isSelected={props.id === context.selectedStitchingItemId}
			onItemClicked={context.handleStitchingItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getStitchingItemName(id: string): string {
	return CloudData.getName(id, CloudData.seatStitchingOptions);
}
